import { useEffect, useRef } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { ApiChannel, channelService } from "../services/channel-service"

export interface ChannelWithBillingIssue {
  channel: ApiChannel
  message: string
  resolveUrl: string | null
}

const CHANNELS_QUERY_KEY = ["channels", "billing-issue"]
const RECHECK_STALE_MS = 10 * 60 * 1000

/**
 * Accept only https facebook.com URLs before rendering the resolve link.
 * The backend already sanitizes, but the URL is webhook-influenced, so the
 * anchor must never carry an unexpected scheme or destination.
 */
function safeMetaUrl(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const parsed = new URL(url)
    const host = parsed.hostname.toLowerCase()
    if (parsed.protocol !== "https:") return null
    if (host !== "facebook.com" && !host.endsWith(".facebook.com")) return null
    return url
  } catch {
    return null
  }
}

/**
 * First channel currently flagged with a Meta billing issue (error 131042 /
 * currency not configured). While flagged, Meta silently drops every paid
 * template message (reminders, broadcasts), so the header must surface it.
 *
 * While the flag is up, this hook also re-verifies against Meta (at most
 * every 10 minutes; the backend throttles further), so the banner disappears
 * on its own shortly after the customer fixes billing on Meta.
 */
export function useChannelBillingIssue(): ChannelWithBillingIssue | null {
  const queryClient = useQueryClient()
  const lastRecheckAttemptRef = useRef(0)

  const { data } = useQuery({
    queryKey: CHANNELS_QUERY_KEY,
    queryFn: () => channelService.getChannels(),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })

  const flagged = (data ?? []).find(channel => !channel.is_deleted && channel.billing_issue)

  useEffect(() => {
    const issue = flagged?.billing_issue
    if (!flagged || !issue) return

    const lastChecked = issue.last_checked_at ?? issue.detected_at
    const flagIsStale =
      !lastChecked || Date.now() - new Date(lastChecked).getTime() > RECHECK_STALE_MS
    const recentlyAttempted = Date.now() - lastRecheckAttemptRef.current < RECHECK_STALE_MS
    if (!flagIsStale || recentlyAttempted) return

    lastRecheckAttemptRef.current = Date.now()
    channelService
      .checkWhatsappBusinessBilling(flagged.id)
      .then(result => {
        if (result.status !== "billing_issue") {
          queryClient.invalidateQueries({ queryKey: CHANNELS_QUERY_KEY })
        }
      })
      .catch(() => {
        // Best-effort: the flag stays until a later re-check or template delivery.
      })
  }, [flagged, queryClient])

  if (!flagged?.billing_issue) return null

  return {
    channel: flagged,
    message: flagged.billing_issue.message,
    resolveUrl: safeMetaUrl(flagged.billing_issue.resolve_url),
  }
}
